import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import appConfig from '../config/appConfig';

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500; // ms

/**
 * Các endpoint KHÔNG được kích hoạt nhánh "401 → đi refresh token".
 *
 * Chính lời gọi refresh mà 401 (refresh token hết hạn / bị thu hồi) thì đi refresh
 * tiếp là đệ quy vô hạn — sự cố 17/09/2026: 1.179 request /auth/refresh trong 60
 * giây làm cháy hạn mức 1.200 req/5 phút, mọi API sau đó trả 429. Đăng nhập sai
 * mật khẩu cũng trả 401 và cũng không có gì để refresh.
 *
 * `refreshAccessToken` nay gọi bằng axios trần nên không còn đi qua đây nữa, nhưng
 * giữ lớp chặn này làm hàng rào: bất kỳ chỗ nào lỡ gọi `api.post('/auth/refresh')`
 * cũng không dựng lại được vòng lặp.
 */
const NO_REFRESH_PATHS = ['/auth/refresh', '/auth/login', '/auth/logout'];
const skipsTokenRefresh = (url = '') => NO_REFRESH_PATHS.some((p) => url.includes(p));

const api = axios.create({
  baseURL: appConfig.getApiUrl(),
  timeout: 120000,
  withCredentials: true, // Gửi httpOnly cookies (refreshToken) trong mọi request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh + auto-retry on network/5xx errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error (no response) — retry with backoff
    if (!error.response) {
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < MAX_RETRIES && originalRequest.method !== 'get') {
        // Only retry non-GET mutating requests if they are idempotent (PUT) or safe to retry
        // For POST, skip retry to avoid duplicates
        if (originalRequest.method === 'put' || originalRequest.method === 'delete' || originalRequest.method === 'patch') {
          originalRequest._retryCount = retryCount + 1;
          await new Promise(r => setTimeout(r, RETRY_DELAY * originalRequest._retryCount));
          return api(originalRequest);
        }
      }
      if (retryCount < MAX_RETRIES && originalRequest.method === 'get') {
        originalRequest._retryCount = retryCount + 1;
        await new Promise(r => setTimeout(r, RETRY_DELAY * originalRequest._retryCount));
        return api(originalRequest);
      }
      console.warn('[API] Network error — no response from server after retries');
      return Promise.reject(error);
    }

    // 5xx server error — retry with backoff (for GET and idempotent requests)
    if (error.response?.status >= 500 && error.response?.status !== 501) {
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < MAX_RETRIES && (originalRequest.method === 'get' || originalRequest.method === 'put')) {
        originalRequest._retryCount = retryCount + 1;
        await new Promise(r => setTimeout(r, RETRY_DELAY * originalRequest._retryCount));
        return api(originalRequest);
      }
    }

    // Kiểm tra TOKEN_VERSION_MISMATCH trước - không retry
    if (error.response?.data?.code === 'TOKEN_VERSION_MISMATCH') {
      console.log('[API] Token version mismatch detected - forcing logout');
      const { forceLogout } = useAuthStore.getState();
      forceLogout(error.response.data.message || 'Thông tin đăng nhập đã thay đổi');
      return Promise.reject(error);
    }

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !skipsTokenRefresh(originalRequest.url)) {
      console.log('[API] Got 401, attempting token refresh...');
      originalRequest._retry = true;

      try {
        const { refreshAccessToken, isAuthenticated } = useAuthStore.getState();
        
        // Nếu không authenticated, không cần refresh
        if (!isAuthenticated) {
          console.log('[API] Not authenticated, skipping refresh');
          return Promise.reject(error);
        }
        
        const success = await refreshAccessToken();

        if (success) {
          const { accessToken } = useAuthStore.getState();
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('[API] Refresh token failed:', refreshError);
      }
      
      // Nếu refresh thất bại, error đã được handle bởi refreshAccessToken
    }

    return Promise.reject(error);
  }
);

export default api;
